import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { REVALIDATE_MODES } from '@savgroup-front-common/constants';
import { toast } from '@savgroup-front-common/core/atoms/Toast';
import { countryList } from '@savgroup-front-common/core/domains/i18n/selectors';
import { BaseAutocompleteOptionForHookForm } from '@savgroup-front-common/types';
import { CompanySuggestion } from '@savgroup-front-common/types/CompanyDirectory';

import { PickedCompany } from '../../../OnboardingActivities/OnboardingActivities.types';
import { findCountryOption } from '../OnboardingForm.helpers';
import OnboardingFormSchema from '../OnboardingForm.schema';

import messages from './messages';
import { useAutoCName } from './useAutoCName';
import useUpsertSalesLeadCommand from './useUpsertSalesLeadCommand';

export interface OnboardingFormFieldValues {
  email: string;
  lastName: string;
  firstName: string;
  companyName: string;
  cName: string;
  phone: string;
  phoneCountryCode: string;
  cgv: boolean;
  address: string;
  postalCode: string;
  city: string;
  countryCode: BaseAutocompleteOptionForHookForm;
}

export interface HandleSelectAddressValues {
  countryCode?: string;
  streetNumber?: string;
  street?: string;
  city?: string;
  postalCode?: string;
}

const useOnboardingFormContext = ({
  handleUserEmail,
}: {
  handleUserEmail: (userEmail: string) => void;
}) => {
  const countryOptions = useSelector(countryList);

  const formContext = useForm<OnboardingFormFieldValues>({
    resolver: yupResolver(OnboardingFormSchema()),
    mode: REVALIDATE_MODES.ON_CHANGE,
    reValidateMode: REVALIDATE_MODES.ON_CHANGE,
  });

  const [pickedCompany, setPickedCompany] = useState<PickedCompany>();
  const [companyWithRegisterAddress, setCompanyWithRegisterAddress] =
    useState<CompanySuggestion>();

  const { handleSubmitSalesLead, isLoading } = useUpsertSalesLeadCommand();
  const autoCName = useAutoCName({ formContext });

  const { setValue } = formContext;

  const handleSelectAddress = useCallback(
    ({ countryCode, city, postalCode }: HandleSelectAddressValues = {}) => {
      const matchingCountry = findCountryOption({
        countryOptions,
        countryCode,
      });

      if (matchingCountry) {
        setValue('countryCode', matchingCountry, { shouldValidate: true });
      }
      setValue('city', city || '');
      setValue('postalCode', postalCode || '');
    },
    [countryOptions, setValue],
  );

  const handleSelectCompany = useCallback(
    (suggestion: CompanySuggestion) => {
      setValue('companyName', suggestion.name, { shouldValidate: true });
      setPickedCompany({
        source: suggestion.source,
        registrationNumber: suggestion.registrationNumber,
      });

      if (!suggestion.address) {
        setCompanyWithRegisterAddress(undefined);

        return;
      }

      setCompanyWithRegisterAddress(suggestion);
      setValue('address', suggestion.address.address || '');
      handleSelectAddress(suggestion.address);
    },
    [handleSelectAddress, setValue],
  );

  const clearPickedCompany = useCallback(() => {
    setPickedCompany(undefined);
    setCompanyWithRegisterAddress(undefined);
  }, []);

  const { resolveCName, isCNameEdited } = autoCName;

  const handleFormSubmit = useCallback(
    async (data: OnboardingFormFieldValues) => {
      const resolvedCName = await resolveCName(data.companyName);

      if (!resolvedCName) {
        if (!isCNameEdited) {
          toast.error(messages.customerSpaceAddressError);
        }

        return;
      }

      handleSubmitSalesLead({
        ...data,
        cName: resolvedCName.cName,
        pickedCompany,
      });
      handleUserEmail(data.email);
    },
    [
      handleSubmitSalesLead,
      handleUserEmail,
      isCNameEdited,
      pickedCompany,
      resolveCName,
    ],
  );

  return {
    formContext,
    countryOptions,
    handleFormSubmit,
    handleSelectAddress,
    handleSelectCompany,
    companyWithRegisterAddress,
    pickedCompany,
    clearPickedCompany,
    autoCName,
    isLoading,
  };
};

export default useOnboardingFormContext;
